import React, { useState } from "react";
import PropTypes from "prop-types";
import Card from "../../../Card";
import { Col, Row } from "@pds-react/grid";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
} from "@tanstack/react-table";
import Select from "react-select";
import { customStyles } from "../../../helpers";

const ParticipatingTrustsTableComponent = ({ ...props }) => {
  const { data } = props;
  const [pageViewNum, setPageViewNum] = useState({ value: "", label: "" });
  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("participating_trust_name", {
      header: () => <span>Name of Participating Trust</span>,
    }),
    columnHelper.accessor("account_type", {
      header: () => <span>Account Type</span>,
    }),
    columnHelper.accessor("sub_account_number", {
      header: () => <span>Sub-Account Number</span>,
    }),
    columnHelper.accessor("transfer_agent_account_number", {
      header: () => <span>Trading Account Number</span>,
    }),
    columnHelper.accessor("actions", {
      header: () => <span>Actions</span>,
      cell: (info) => <p>{info.getValue()}</p>,
    }),
  ];
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  const getPageViewOption = (numberOfPages) => {
    let choices = [];
    for (let i = 1; i < numberOfPages + 1; i++) {
      choices.push({ value: i - 1, label: i });
    }
    return choices;
  };

  return (
    <>
      <Col sm={12} lg={12} className="util-margin-top-sm-15 util-margin-top-xs-30">
        <Row className="util-margin-top-sm-15 util-margin-top-xs-30">
          <Col sm={12} lg={12}>
            <Card heading={"Associated Plans"}>
              <table data-testid={"participating_trusts_table"} id={"participating_trusts_table"}>
                <colgroup>
                  <col style={{ width: 20 }} />
                  <col style={{ width: 20 }} />
                  <col style={{ width: 20 }} />
                  <col style={{ width: 20 }} />
                  <col style={{ width: 20 }} />
                </colgroup>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.length > 0 ? (
                <Row>
                  <Col sm={6} lg={6}></Col>
                  <Col sm={6} lg={6}>
                    <Row>
                      <button
                        data-testid={"pagination-first"}
                        className="border rounded p-1"
                        onClick={() => {
                          table.setPageIndex(0);
                          setPageViewNum({
                            value: 0,
                            label: 1,
                          });
                        }}
                        disabled={!table.getCanPreviousPage()}
                      >
                        {"<<"}
                      </button>
                      <button
                        data-testid={"pagination-back"}
                        className="border rounded p-1"
                        onClick={() => {
                          table.previousPage();
                          setPageViewNum({
                            value: table.getState().pagination.pageIndex - 1,
                            label: table.getState().pagination.pageIndex,
                          });
                        }}
                        disabled={!table.getCanPreviousPage()}
                      >
                        {"<"}
                      </button>
                      <div data-testid={"pagination-page-select"}>
                        <Select
                          isClearable={true}
                          placeholder={"Go to page"}
                          id={"page_select"}
                          inputId={"page_select"}
                          options={getPageViewOption(table.getPageCount())}
                          class_name="page_select"
                          styles={customStyles}
                          value={pageViewNum["label"] !== "" ? pageViewNum : "undefined"}
                          onChange={(e) => {
                            let pageView = e;
                            let selectedPage = 0;
                            if (pageView === null) {
                              pageView = { value: "", label: "" };
                            } else {
                              selectedPage = pageView["value"];
                            }
                            setPageViewNum(pageView);
                            table.setPageIndex(selectedPage);
                          }}
                        />
                      </div>
                      <div className={"page_text"}>{` of ${table.getPageCount()} `}</div>
                      <button
                        data-testid={"pagination-next"}
                        className="border rounded p-1"
                        onClick={() => {
                          table.nextPage();
                          setPageViewNum({
                            value: table.getState().pagination.pageIndex + 1,
                            label: table.getState().pagination.pageIndex + 2,
                          });
                        }}
                        disabled={!table.getCanNextPage()}
                      >
                        {">"}
                      </button>
                      <button
                        data-testid={"pagination-last"}
                        className="border rounded p-1"
                        onClick={() => {
                          table.setPageIndex(table.getPageCount() - 1);
                          setPageViewNum({
                            value: table.getPageCount() - 1,
                            label: table.getPageCount(),
                          });
                        }}
                        disabled={!table.getCanNextPage()}
                      >
                        {">>"}
                      </button>
                      <div data-testid={"pagination-number-of-rows"}>
                        <select
                          value={table.getState().pagination.pageSize}
                          onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                          }}
                        >
                          {[10, 25, 50, 75, 100].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                              Show {pageSize}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Row>
                  </Col>
                </Row>
              ) : (
                <></>
              )}
            </Card>
          </Col>
        </Row>
      </Col>
    </>
  );
};

export default ParticipatingTrustsTableComponent;

ParticipatingTrustsTableComponent.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
};
